import { useMemo, type VoidFunctionComponent } from 'react';
import Subnav from '../../generic/Subnav';
import { useCanDisplaySecurityOptions } from '../generic/hooks';
import { ReportCategories, ReportCategoryTitles } from '../generic/ReportGeneration/utils';
import { DISABLE_REPORT_UI_REFRESH, FULL_SECURITY } from '../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled, useIsSecurityEnabled } from '../../../helpers/features';
import { isFreeTier } from '../../../../shared/subscriptions';
import { window } from '../../../globals';
import type { ReportCategory } from '../generic/shared/types';
import { vulnManagementFeature } from '../../settings/billing/features/consts';

// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style -- Prefer an index signature to make the key purpose clear.
type EnabledReports = {
  [reportType: string]: {
    title: string;
    isPremiumOnly: boolean;
  }
};

/**
 * Some reports might be completely restricted to only premium orgs. This Set tracks those.
 */
const premiumOnlyReports = new Set<ReportCategory>([
  ReportCategories.RemediationGuidance,
]);

/**
 * Mini hook which just memoizes the enabled report types based on the
 * security and PDF reports feature flag.
 */
const useEnabledReportTypes = (): EnabledReports => {
  const isSecurityAndPDFReportsEnabled = useCanDisplaySecurityOptions();
  const isSecurityEnabled = useIsSecurityEnabled();

  return useMemo(() => {
    const excludedReports: string[] = [];
    if (!isSecurityAndPDFReportsEnabled) {
      excludedReports.push(ReportCategories.Vulnerability);
    }
    if (!isSecurityEnabled) {
      excludedReports.push(ReportCategories.RemediationGuidance);
    }

    return Object.entries(ReportCategoryTitles).reduce<EnabledReports>((acc, [reportType, title]) => {
      if (excludedReports.includes(reportType)) {
        return acc;
      }

      acc[reportType] = {
        title,
        isPremiumOnly: premiumOnlyReports.has(reportType as ReportCategory),
      };
      return acc;
    }, {});
  }, [isSecurityAndPDFReportsEnabled, isSecurityEnabled]);
};

type Props = {
  baseRevisionURL: string;
};

/**
 * ProjectSubnavItemReports is the dropdown subnav item for project Reports.
 */
const ProjectSubnavItemReports: VoidFunctionComponent<Props> = ({ baseRevisionURL }) => {
  const isReportUIRefreshEnabled = !useIsFeatureEnabled(DISABLE_REPORT_UI_REFRESH);
  const shouldShowSettings = isReportUIRefreshEnabled && !isFreeTier({
    plan: window?.user?.organization?.stripe_plan,
    stripePriceLimit: window?.user?.organization?.stripePriceLimit,
  });
  const enabledReportTypes = useEnabledReportTypes();

  const isFullSecurityEnabled = useIsFeatureEnabled(FULL_SECURITY);

  return (
    <Subnav.Dropdown to={`${baseRevisionURL}/reports`} navKey="reports">
      <Subnav.Dropdown.RootItem>Reports</Subnav.Dropdown.RootItem>
      {Object.entries(enabledReportTypes).map(([reportType, data]) => {
        if (data.isPremiumOnly) {
          return (
            <Subnav.Dropdown.PaidFeatureItem
              key={`reports/${reportType}`}
              to={`${baseRevisionURL}/reports/${reportType}`}
              navKey={`reports/${reportType}`}
              modalFocusFeature={vulnManagementFeature}
              isRestricted={!isFullSecurityEnabled}
            >
              {data.title}
            </Subnav.Dropdown.PaidFeatureItem>
          );
        }

        return (
          <Subnav.Dropdown.Item
            key={`reports/${reportType}`}
            to={`${baseRevisionURL}/reports/${reportType}`}
            navKey={`reports/${reportType}`}
          >
            {data.title}
          </Subnav.Dropdown.Item>
        );
      },
      )}
      {shouldShowSettings && (
        <Subnav.Dropdown.Item
          key="reports/settings"
          to={`${baseRevisionURL}/reports/settings`}
          navKey="reports/settings"
        >
          Settings
        </Subnav.Dropdown.Item>
      )}
    </Subnav.Dropdown>
  );
};

export default ProjectSubnavItemReports;
